import "./fieldGuide/critters.scss";
import { createApp, toRaw } from "vue/dist/vue.esm-bundler.js"; //@todo convert into proper vue app w/o twig template
import CrittersResults from "./fieldGuide/CrittersResults.vue";
import Reveal from "./common/Reveal.vue";
import Combobox from "components/combobox/Combobox.vue";

const container = document.getElementById("js-critters");
const filtersDefault = {
  s: "",
  fieldGuideType: [],
  fieldGuideHabitat: [],
  nativeStatus: [],
  conservationStatus: [],
};
const dataContainer = document.getElementById("js-data");
const types = JSON.parse(dataContainer.dataset.fieldGuideTypes);

const app = createApp({
  delimiters: ["${", "}"],
  components: {
    CrittersResults,
    Reveal,
    Combobox,
  },
  data() {
    return {
      elementsPerPage: 9,
      error: null,
      filters: { ...filtersDefault },
      totalResults: null,
      searchQuery: "",
      showControls: false,
      showLoadMore: false,
      shownResults: null,
    };
  },
  types: types,
  mounted: function () {
    //check for any URL params and add them to our filters var
    const urlParams = new URLSearchParams(window.location.search);
    for (let v of urlParams) {
      let key = v.shift();
      if (key !== "s") {
        if (Array.isArray(v)) v = v[0];
        //check if 'v' is a category group name
        if (types.hasOwnProperty(v)) {
          if (typeof types[v] === "object")
            types[v].categories.forEach((type) => this.filters[key].push(type));
          else this.filters[key].push({ id: types[v], text: v });
        } else {
          const subType = this.findNested(types, "text", v);
          if (typeof subType === "object") {
            this.filters[key].push(subType);
          }
        }
        if (key === "nativeStatus" || key === "conservationStatus") {
          this.filters[key].push({ text: v });
        }
      }
    }
    //search
    if (urlParams.has("s") && urlParams.get("s") !== "") {
      this.filters.s = urlParams.get("s");
    }
    //filter sidebar
    const hash = window.location.hash;
    if (hash === "#showfilters") {
      this.showControls = true;
    }

    //Add event listener for window pop state to change query/filters
    addEventListener("popstate", this.popState);
  },
  unmounted() {
    removeEventListener("popstate", this.popState);
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        // set up new url search params object
        let filterParams = new URLSearchParams();

        // setting up data from this.filters to iterate through
        let entries = Object.entries(this.filters);

        // search term must be read separately from other filters (string vs nested array)
        let i = 0;
        for (i = 0; i < entries.length; i++) {
          let filterValues = entries[i][1];
          if (entries[i][0] !== "s") {
            filterValues.forEach((element) =>
              filterParams.append(entries[i][0], element["text"])
            );
          } else {
            filterParams.append("s", this.filters["s"]);
          }
        }

        //create a new url object from the current url
        let url = new URL(window.location.href);

        //sets the query params and replaces the url in the browser
        url.search = filterParams.toString();
        this.pushState(url);
      },
    },
    showControls: function (show) {
      const url = new URL(window.location.href);
      url.hash = show ? "#showfilters" : "";
      this.pushState(url);
    },
  },
  methods: {
    search: function () {
      if (this.searchQuery === "") return;
      this.clearAllFilters();
      this.filters.s = this.searchQuery;
      this.showControls = false;
      this.searchQuery = "";
    },
    removeFilter: function (filter) {
      if (filter.group === "s") this.filters.s = "";
      else {
        const index = this.filters[filter.group].findIndex(
          (f) => f.text === filter.value
        );
        if (index !== -1) {
          this.filters[filter.group].splice(index, 1);
        }
      }
    },
    clearAllFilters: function () {
      for (const [key, value] of Object.entries(this.filters)) {
        this.filters[key] = Array.isArray(value) ? [] : "";
      }
    },
    isAllTypesChecked: function (type, group) {
      return types[type].categories.every((question) =>
        this.filters[group].some((filter) => filter.text === question.text)
      );
    },
    toggleAllSubTypes: function (group, type, e) {
      if (e.target.checked) {
        //just checked it, so add them all
        this.filters[group].push(...types[type].categories);
      } else {
        //unchecked, so remove
        types[type].categories.forEach((filter) =>
          this.removeFilter({ value: filter.text, group: group })
        );
      }
      this.filters[group] = [
        ...new Map(this.filters[group].map((v) => [v.id, v])).values(),
      ];
    },
    findNested: function (obj, key, value) {
      // Base case
      if (obj[key] === value) {
        return obj;
      } else {
        const keys = Object.keys(obj); // add this line to iterate over the keys

        for (let i = 0, len = keys.length; i < len; i++) {
          let k = keys[i]; // use this key for iteration, instead of index "i"

          // add "obj[k] &&" to ignore null values
          if (obj[k] && typeof obj[k] == "object") {
            const found = this.findNested(obj[k], key, value);
            if (found) {
              // If the object was found in the recursive call, bubble it up.
              return found;
            }
          }
        }
      }
    },
    generateState: function () {
      return {
        filters: JSON.parse(JSON.stringify(this.filters)),
        showControls: toRaw(this.showControls),
      };
    },
    popState: function (e) {
      if (e.state === null) {
        this.showControls = false;
        this.filters = { ...filtersDefault };
        return;
      }
      this.showControls = e.state.hasOwnProperty("showControls")
        ? e.state.showControls
        : false;
      if (e?.state.hasOwnProperty("filters")) {
        this.filters = e.state.filters;
      } else this.filters = { ...filtersDefault };
    },
    pushState: function (url) {
      if (window.location.href === url.toString()) return;
      const currentState = this.generateState();
      window.history.pushState(currentState, "", url.toString());
    },
  },
  computed: {
    filtersFlat() {
      const filters = [];
      for (const [key, value] of Object.entries(this.filters)) {
        if (Array.isArray(value)) {
          value.forEach((v) => {
            if (v.text) {
              filters.push({
                group: key,
                value: v.text,
              });
            }
          });
        }
      }
      if (this.filters.s.length > 0) {
        filters.push({ group: "s", value: this.filters.s });
      }
      return filters;
    },
    queryFilters() {
      const newFilters = JSON.parse(JSON.stringify(this.filters));
      const group = "fieldGuideType";

      for (const [key, value] of Object.entries(types)) {
        if (typeof types[key] === "object") {
          if (this.isAllTypesChecked(key, group)) {
            newFilters[group].push({ id: value.id });
          }
        }
      }
      return newFilters;
    },
  },
});
app.mount(container);
